import Stepper from '@/shared/components/Stepper';
import {IMPORT_CONTACT_LIST, SIDEBAR} from '@/utils/constants';
import {Box} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import ContactsUploader from './ContactsUploader';
import {UploadContactListContext, UploadContactListContextType} from '../../UploadContactListContext';
import UploadedContactListTable from './UploadedContactListTable';
import MatchColumns from './MatchColumns';
import ValidateDataTable from './ValidateDataTable';
import Modal from '@/shared/components/Modal';
import {contactListTableStyle} from '../ContactListTable.styles';

interface ImportContactListProps {
	onCloseModal: () => void;
	open: boolean;
	handleErrorModal: () => void;
}

export const contactListSteps = [
	`1. ${SIDEBAR.UPLOADED_FILE}`,
	`2. ${SIDEBAR.SELECT_HEADER_ROW}`,
	`3. ${SIDEBAR.MATCH_COLUMNS}`,
	`4. ${SIDEBAR.VALIDATE_DATA}`,
];

export enum CONTACT_LIST_STEP {
	UPLOADED_FILE,
	SELECT_HEADER_ROW,
	MATCH_COLUMNS,
	VALIDATE_DATA,
}

export const ImportContactList: FC<ImportContactListProps> = ({onCloseModal, open, handleErrorModal}) => {
	const [activeStep, setActiveStep] = useState(0);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [sheetName, setSheetName] = useState<string>('');
	const [headerIndex, setHeaderIndex] = useState<number | null>(null);
	const [existingContactList, setExistingContactList] = useState<Record<string, any>>([]);
	const [manageDuplicate, setManageDuplicate] = useState<string>('');
	const [columnForDuplicate, setColumnForDuplicate] = useState<string>('');

	const handleNextStep = () => setActiveStep(activeStep + 1);

	const formData = useMemo(() => {
		switch (activeStep) {
			case CONTACT_LIST_STEP.UPLOADED_FILE:
				return <ContactsUploader />;

			case CONTACT_LIST_STEP.SELECT_HEADER_ROW:
				return <UploadedContactListTable />;

			case CONTACT_LIST_STEP.MATCH_COLUMNS:
				return <MatchColumns />;

			case CONTACT_LIST_STEP.VALIDATE_DATA:
				return <ValidateDataTable onClose={onCloseModal} />;

			default:
				return <ContactsUploader />;
		}
	}, [activeStep]);

	const UploadContactListContextValue: UploadContactListContextType = useMemo(() => {
		return {
			activeStep,
			onCloseModal,
			handleNextStep,
			selectedFile,
			setSelectedFile,
			sheetName,
			setSheetName,
			headerIndex,
			setHeaderIndex,
			existingContactList,
			setExistingContactList,
			manageDuplicate,
			setManageDuplicate,
			columnForDuplicate,
			setColumnForDuplicate,
		};
	}, [activeStep, onCloseModal, handleNextStep]);

	const handleShowErrorModal = () => {
		if (activeStep === CONTACT_LIST_STEP.UPLOADED_FILE && selectedFile === null) {
			onCloseModal();
		} else handleErrorModal();
	};

	return (
		<UploadContactListContext.Provider value={UploadContactListContextValue}>
			<Modal open={open} onClose={handleShowErrorModal} sx={contactListTableStyle.modal} title={IMPORT_CONTACT_LIST}>
				<Box>
					<Stepper steps={contactListSteps} activeStep={activeStep} sx={contactListTableStyle.stepper} />
					{formData}
				</Box>
			</Modal>
		</UploadContactListContext.Provider>
	);
};

export default ImportContactList;
